import React, { useRef, useEffect } from "react";
import { Card } from "@mui/material";
import Editor from "@monaco-editor/react";
import Div from "@jumbo/shared/Div";
import { TabPanel } from "components/TabPanel";
import { StyledTab, StyledTabs } from "components/CustomTabs";
import { debounce } from "lodash";

const MobileCodeEditorAndOutputPanel = ({
  code,
  setCode,
  editorLanguage,
  headerControls,
  codeTabs,
  outputTabs,
  outputContent,
  onEditorMount,
  theme,
  codeTabValue,
  setCodeTabValue,
  ioTabValue,
  handleIOTabChange,
}) => {
  const resizeObserverRef = useRef(null);

  const handleEditorMount = (editor, monaco) => {
    if (onEditorMount) onEditorMount(editor, monaco);
    const container = editor.getDomNode()?.parentElement;
    if (container) {
      resizeObserverRef.current = new ResizeObserver(
        debounce(() => {
          editor.layout();
        }, 200)
      );
      resizeObserverRef.current.observe(container);
    }
  };

  useEffect(() => {
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, []);

  return (
    <Div
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        boxSizing: "border-box",
        width: "100%",
      }}
    >
      <Card
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          borderRadius: 0,
          overflow: "hidden",
          maxHeight: 300,
        }}
      >
        <Div
          sx={{
            borderTop: 1,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
          }}
        >
          <StyledTabs
            value={codeTabValue}
            onChange={(_, newValue) => setCodeTabValue(newValue)}
            sx={{ flexGrow: 1 }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label="Code panel tabs"
          >
            {codeTabs.map((tab, index) => (
              <StyledTab
                key={index}
                icon={tab.icon}
                iconPosition="start"
                label={tab.label}
                aria-label={tab.label}
              />
            ))}
          </StyledTabs>
          {headerControls && <Div sx={{ mr: 1 }}>{headerControls}</Div>}
        </Div>
        <Div sx={{ flexGrow: 1, overflow: "auto", height: 200 }}>
          {codeTabs.map((tab, index) => (
            <TabPanel
              key={index}
              value={codeTabValue}
              index={index}
              sx={{ p: 0, height: "100%", boxSizing: "border-box" }}
            >
              {index === 0 ? (
                <Editor
                  height="100%"
                  width="100%"
                  theme={theme.mode === "dark" ? "vs-dark" : "light"}
                  language={editorLanguage}
                  value={code}
                  onChange={setCode}
                  onMount={handleEditorMount}
                  options={{
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    wordWrap: "on",
                    folding: true,
                    renderWhitespace: "none",
                  }}
                />
              ) : (
                tab.content
              )}
            </TabPanel>
          ))}
        </Div>
      </Card>
      <Card
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          borderRadius: 0,
          overflow: "hidden",
          maxHeight: 300,
        }}
      >
        <Div
          sx={{
            borderTop: 1,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
          }}
        >
          <StyledTabs
            value={ioTabValue}
            onChange={(_, newValue) => handleIOTabChange(_, newValue)}
            sx={{ flexGrow: 1 }}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="Output panel tabs"
          >
            {outputTabs.map((tab, index) => (
              <StyledTab
                key={index}
                icon={tab.icon}
                iconPosition="start"
                label={tab.label}
                aria-label={tab.label}
              />
            ))}
          </StyledTabs>
        </Div>
        <Div sx={{ flexGrow: 1, overflow: "auto" }}>
          {outputTabs.map((tab, index) => (
            <TabPanel
              key={index}
              value={ioTabValue}
              index={index}
              sx={{ p: 0 }}
            >
              {outputContent[index]}
            </TabPanel>
          ))}
        </Div>
      </Card>
    </Div>
  );
};

export default MobileCodeEditorAndOutputPanel;
